import { Tooltip, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { PatternsLabels } from 'src/constants/pattern';
import { useUser } from 'src/context/UserProvider';
import { usePattern } from 'src/hook/usePattern';
import { WebGLRenderer } from 'three';
import * as THREE from 'three';

import { StateNames, States } from '../../../../Screens/Configurator';
import { EngravingItem } from './Engravings/EngravingItem';
import { KanvasEditor } from './KanvasEditor';
import { Catalog } from './Patterns/Catalog';
import { PatternItem } from './Patterns/PatternItem';

type ViewOption = {
  key: 'x' | 'y' | 'z' | 'free';
  inverse: boolean;
  label: string;
  icon: string;
};

const VIEW_OPTIONS: ViewOption[] = [
  {
    key: 'free',
    inverse: false,
    label: 'Vue globale',
    icon: '/faces/none.png',
  },
  { key: 'z', inverse: false, label: 'Avant', icon: '/faces/front.png' },
  { key: 'x', inverse: false, label: 'Gauche', icon: '/faces/left.png' },
  { key: 'x', inverse: true, label: 'Droite', icon: '/faces/right.png' },
  { key: 'z', inverse: true, label: 'Arrière', icon: '/faces/back.png' },
  { key: 'y', inverse: false, label: 'Dessus', icon: '/faces/up.png' },
  { key: 'y', inverse: true, label: 'Dessous', icon: '/faces/down.png' },
];

type PatternsProps = {
  pieces: any[];
  pieceIndex: number;
  setPatterns: (patterns: { [key: string]: { [key: string]: any }[] }) => void;
  patterns: {
    [key: string]: { [key: string]: any }[];
  };
  selectedPattern: number | undefined;
  setSelectedPattern: (selectedPattern: number | undefined) => void;
  orbitRef: React.MutableRefObject<any>;
  centerCamera: () => void;
  setValue: <T extends StateNames>(stateName: T, value: States[T]) => void;
  gl: WebGLRenderer | undefined;
  orientation: {
    key: 'x' | 'y' | 'z' | 'free';
    inverse: boolean;
  };
  setOrientation: (orientation: {
    key: 'x' | 'y' | 'z' | 'free';
    inverse: boolean;
  }) => void;
  selectedPage: string;
  customSteles: any[];
  customPlatings: any[];
  customSoles: any[];
};

export const PatternsEngraving = ({
  pieces,
  pieceIndex,
  setPatterns,
  patterns,
  setSelectedPattern,
  selectedPattern,
  orbitRef,
  centerCamera,
  setValue,
  gl,
  orientation,
  setOrientation,
  selectedPage,
  customPlatings,
  customSteles,
  customSoles,
}: PatternsProps) => {
  const shouldUpdateTexture = useRef<{
    pieceUUID: string;
    orientation: string;
  } | null>(null);

  const {
    kanvasSize,
    selectedImportPage,
    setSelectedImportPage,
    setSelectedPiece,
    isButtonOpen,
    setIsButtonOpen,
    selectedPiece,
    updateKanvas,
    changePattern,
    applyPatternsTmp,
    getPatterns,
    addText,
    changeText,
  } = usePattern({
    pieces,
    customSteles,
    customPlatings,
    pieceIndex,
    setPatterns,
    patterns,
    orbitRef,
    centerCamera,
    setValue,
    gl,
    orientation,
    setOrientation,
    shouldUpdateTexture,
    customSoles,
  });
  const { user } = useUser();

  const stageRef = useRef<any>();

  const [isPatternsLoaded, setIsPatternsLoaded] = useState<{
    [key: string]: boolean;
  }>({});

  const updateTexture = (pieceUUID: string, orientation: string) => {
    const patternsTmp = { ...patterns };

    if (
      stageRef.current &&
      patternsTmp[pieceUUID] &&
      patternsTmp[pieceUUID][orientation]
    ) {
      const canvas = stageRef.current.toCanvas();
      const texture = new THREE.CanvasTexture(canvas);
      patternsTmp[pieceUUID][orientation].texture = texture;
    }
    setPatterns(patternsTmp);
  };

  const isTexturesLoading = () => {
    const patterns = getPatterns();
    for (let i = 0; i < patterns.length; i += 1) {
      if (isPatternsLoaded[i]) return true;
    }
    return false;
  };

  useEffect(() => {
    setIsButtonOpen(false);
    setOrientation({ key: 'free', inverse: false });
  }, [selectedPage, setIsButtonOpen, setOrientation]);

  useEffect(() => {
    if (getPatterns().length > 0 && selectedPattern === undefined)
      setSelectedPattern(0);
  }, [getPatterns, orientation, selectedPattern, setSelectedPattern]);

  useEffect(() => {
    if (
      shouldUpdateTexture &&
      shouldUpdateTexture.current !== null &&
      shouldUpdateTexture.current.pieceUUID &&
      shouldUpdateTexture.current.orientation
    ) {
      const { pieceUUID } = shouldUpdateTexture.current;
      const { orientation } = shouldUpdateTexture.current;
      updateTexture(pieceUUID, orientation);
      shouldUpdateTexture.current = null;
    }
  }, [patterns]);

  useEffect(() => {
    setValue('texture', true);
  }, []);

  const menuPaterns = useMemo(() => {
    const sortedLabels = PatternsLabels.sort((a, b) => {
      if (a.label < b.label) return -1;
      if (a.label > b.label) return 1;
      return 0;
    });

    const filteredLabels = sortedLabels.filter((el) => el.key !== 'TMP');
    return filteredLabels;
  }, []);

  const isLoading = isTexturesLoading();
  const isFreeView = orientation.key === 'free';

  return (
    <>
      {isFreeView ? null : (
        <KanvasEditor
          orbitRef={orbitRef}
          kanvasSize={kanvasSize}
          orientation={orientation}
          setPatterns={setPatterns}
          patterns={patterns}
          pieces={pieces}
          pieceIndex={pieceIndex}
          selectedPattern={selectedPattern}
          setSelectedPattern={setSelectedPattern}
          stageRef={stageRef}
          customSteles={customSteles}
          customPlatings={customPlatings}
          shouldUpdateTexture={shouldUpdateTexture}
          isPatternsLoaded={isPatternsLoaded}
          setIsPatternsLoaded={setIsPatternsLoaded}
          customSoles={customSoles}
        />
      )}

      <div className='flex flex-col w-full relative h-full'>
        {selectedImportPage ? (
          <>
            <div className='flex flex-row'>
              <Typography
                variant='body2'
                color='#344054'
                className='ml-[23px] mt-[23px] cursor-pointer z-[2]'
                onClick={() => setSelectedImportPage(undefined)}
              >
                {'Elements >'}
              </Typography>
              <Typography
                variant='body2'
                color='#344054'
                className='mt-[23px] ml-1 !font-bold'
              >
                {
                  PatternsLabels.find((el) => el.key === selectedImportPage)
                    ?.label
                }
              </Typography>
            </div>
            {user ? (
              <Catalog
                setPatterns={setPatterns}
                patterns={patterns}
                pieceIndex={pieceIndex}
                pieces={pieces}
                setSelectedImportPage={setSelectedImportPage}
                setSelectedPiece={setSelectedPiece}
                orientation={orientation}
                selectedPage={selectedPage}
                user={user}
                selectedImportPage={selectedImportPage}
                orbitRef={orbitRef}
                customSteles={customSteles}
                customPlatings={customPlatings}
                customSoles={customSoles}
              />
            ) : null}
          </>
        ) : (
          <>
            <div className='p-3 flex flex-col items-stretch w-full'>
              <div className='flex flex-row items-center gap-2 w-full justify-center'>
                <button
                  className={`flex flex-col items-center justify-center py-[6px] px-1 rounded-md transition-all duration-200 min-h-[44px] gap-[6px] min-w-[100px] ${
                    isFreeView
                      ? 'border-2 border-[#BC9A67] bg-[rgba(188,154,103,0.1)]'
                      : 'border border-[#D0D5DD] bg-white'
                  } ${
                    isLoading
                      ? 'cursor-not-allowed opacity-50'
                      : `cursor-pointer hover:border-[#BC9A67] ${
                          isFreeView
                            ? 'hover:bg-[rgba(188,154,103,0.15)]'
                            : 'hover:bg-[rgba(188,154,103,0.05)]'
                        }`
                  }`}
                  style={{
                    alignSelf: pieceIndex !== -1 ? 'stretch' : 'auto',
                  }}
                  disabled={isLoading}
                  onClick={() => {
                    setSelectedPattern(undefined);
                    setOrientation({ key: 'free', inverse: false });
                  }}
                >
                  <img
                    className='w-[22px] h-[22px] object-contain mb-[2px]'
                    src='/faces/none.png'
                    alt='Vue globale'
                  />
                  <span
                    className={`text-sm text-center leading-[1.1] ${
                      isFreeView
                        ? 'font-semibold text-[#BC9A67]'
                        : 'font-normal text-[#344054]'
                    }`}
                  >
                    Vue globale
                  </span>
                </button>

                {pieceIndex !== -1 && (
                  <div className='grid grid-cols-3 gap-[6px] flex-1'>
                    {VIEW_OPTIONS.filter((view) => view.key !== 'free').map(
                      (view) => {
                        const isSelected =
                          orientation.key === view.key &&
                          orientation.inverse === view.inverse;
                        return (
                          <Tooltip
                            key={`${view.key}-${view.inverse}`}
                            title={view.label}
                            arrow
                          >
                            <button
                              className={`flex flex-row items-center justify-center py-[6px] px-1 rounded-md transition-all duration-200 min-h-[44px] gap-[6px] ${
                                isSelected
                                  ? 'border-2 border-[#BC9A67] bg-[rgba(188,154,103,0.1)]'
                                  : 'border border-[#D0D5DD] bg-white'
                              } ${
                                isLoading
                                  ? 'cursor-not-allowed opacity-50'
                                  : `cursor-pointer hover:border-[#BC9A67] ${
                                      isSelected
                                        ? 'hover:bg-[rgba(188,154,103,0.15)]'
                                        : 'hover:bg-[rgba(188,154,103,0.05)]'
                                    }`
                              }`}
                              disabled={isLoading}
                              onClick={() => {
                                setSelectedPattern(undefined);
                                setOrientation({
                                  key: view.key,
                                  inverse: view.inverse,
                                });
                              }}
                            >
                              <img
                                className='w-[22px] h-[22px] object-contain mb-[2px]'
                                src={view.icon}
                                alt={view.label}
                              />
                              <span
                                className={`text-sm text-center leading-[1.1] ${
                                  isSelected
                                    ? 'font-semibold text-[#BC9A67]'
                                    : 'font-normal text-[#344054]'
                                }`}
                              >
                                {view.label}
                              </span>
                            </button>
                          </Tooltip>
                        );
                      },
                    )}
                  </div>
                )}
              </div>
            </div>

            {getPatterns()?.length > 0 ? (
              <div className='flex w-full h-fit px-[22px]'>
                <Typography
                  variant='body1'
                  color='#344054'
                  className='ml-1 !font-light !text-sm'
                >
                  {`Pour déplacer ${
                    selectedPage === 'patterns' ? ' un motif' : 'une gravure'
                  }, selectionnez une vue, cliquez sur le visuel à gauche ou sur le motif dans la
                  liste, maintenez le clic sur le visuel à gauche et bougez votre souris.`}
                </Typography>
              </div>
            ) : null}

            <div className='relative flex flex-row flex-wrap gap-4 py-4 px-[22px]'>
              {getPatterns()?.length > 0 ? (
                getPatterns()?.map((el: any, elIndex: number) => {
                  if (
                    (selectedPage === 'patterns' && el.type !== 'image') ||
                    (selectedPage === 'engravings' && el.type !== 'text')
                  )
                    return <></>;
                  return selectedPage === 'patterns' ? (
                    <PatternItem
                      key={`pattern_${elIndex}`}
                      shouldUpdateTexture={shouldUpdateTexture}
                      selectedPattern={selectedPattern}
                      elIndex={elIndex}
                      updateKanvas={updateKanvas}
                      setSelectedPattern={setSelectedPattern}
                      el={el}
                      changePattern={changePattern}
                      applyPatternsTmp={applyPatternsTmp}
                      patterns={patterns}
                      setPatterns={setPatterns}
                      orientation={orientation}
                      setOrientation={setOrientation}
                    />
                  ) : (
                    <EngravingItem
                      key={`engraving${elIndex}`}
                      el={el}
                      selectedPattern={selectedPattern}
                      elIndex={elIndex}
                      setSelectedPattern={setSelectedPattern}
                      changeText={changeText}
                      patterns={patterns}
                      setPatterns={setPatterns}
                      orientation={orientation}
                      setOrientation={setOrientation}
                      shouldUpdateTexture={shouldUpdateTexture}
                    />
                  );
                })
              ) : (
                <div className='flex w-full h-full items-center justify-center'>
                  <Typography
                    variant='body1'
                    color='#344054'
                    className='mt-[23px] ml-1 !font-light !text-sm'
                  >
                    {`Cliquez sur une pièce puis choisissez une vue pour ajouter ${
                      selectedPage === 'patterns' ? 'un motif' : 'une gravure'
                    }`}
                  </Typography>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {isFreeView ? null : (
        <div
          className={`absolute w-full h-full pb-[90px] p-[27px] transition-colors duration-500 flex flex-col items-center justify-end top-0 pointer-events-none ${
            isButtonOpen ? 'bg-black/40' : 'bg-transparent'
          }`}
        >
          <div
            className={`${isButtonOpen ? 'flex' : 'hidden'} ${
              PatternsLabels.length > 10
                ? 'flex-row w-full'
                : 'flex-col w-[159px]'
            } flex-wrap justify-center items-start rounded-[10px] bg-white mb-3 z-[2] pointer-events-auto divide-y divide-[#98A2B3]`}
          >
            {selectedPage === 'patterns' ? (
              <>
                {menuPaterns.map((el) => {
                  return (
                    <div
                      key={el.key}
                      className={`py-[10px] px-4 cursor-pointer ${
                        PatternsLabels.length > 10 ? 'w-[33%]' : 'w-[99%]'
                      }`}
                      onClick={() => {
                        setSelectedImportPage(el.key);
                        setIsButtonOpen(false);
                      }}
                    >
                      <Typography
                        variant='body2'
                        color='#344054'
                        className='!text-xs select-none text-center'
                      >
                        {el.label}
                      </Typography>
                    </div>
                  );
                })}
              </>
            ) : null}
          </div>

          {isButtonOpen ? (
            <>
              <div
                className='flex justify-center items-center w-[159px] h-8 bg-white transition-colors duration-500 rounded-[28px] py-2 px-4 cursor-pointer z-[2] pointer-events-auto mb-3'
                onClick={() => {
                  setSelectedImportPage('ALL');
                  setIsButtonOpen(false);
                }}
              >
                <Typography variant='body2' color='black' className='!text-xs'>
                  Tous les motifs
                </Typography>
              </div>
              {user?.role === 'ADMIN' ? (
                <div
                  className='flex justify-center items-center w-[159px] h-8 bg-white transition-colors duration-500 rounded-[28px] py-2 px-4 cursor-pointer z-[2] pointer-events-auto mb-3'
                  onClick={() => {
                    setSelectedImportPage('TMP');
                    setIsButtonOpen(false);
                  }}
                >
                  <Typography
                    variant='body2'
                    color='black'
                    className='!text-xs'
                  >
                    Motifs par défaut
                  </Typography>
                </div>
              ) : null}
            </>
          ) : null}

          {selectedPiece ? (
            <div
              className={`flex justify-center items-center w-[159px] h-8 transition-colors duration-500 rounded-[28px] py-2 px-4 cursor-pointer z-[2] pointer-events-auto ${
                isButtonOpen ? 'bg-white' : 'bg-[#BC9A67]'
              }`}
              onClick={() => {}}
            >
              <Typography
                variant='body2'
                color={isButtonOpen ? 'black' : 'white'}
                className='!text-xs'
              >
                Valider la sélection
              </Typography>
            </div>
          ) : (
            <div
              className={`flex justify-center items-center w-[159px] h-8 transition-colors duration-500 rounded-[28px] py-2 px-4 cursor-pointer z-[2] pointer-events-auto ${
                isButtonOpen ? 'bg-white' : 'bg-[#BC9A67]'
              }`}
              onClick={() => {
                if (selectedPage === 'patterns') setIsButtonOpen(!isButtonOpen);
                if (selectedPage === 'engravings') addText();
              }}
            >
              <Typography
                variant='body2'
                color={isButtonOpen ? 'black' : 'white'}
                className='!text-xs'
              >
                {isButtonOpen ? 'Annuler' : 'Ajouter un élément'}
              </Typography>
            </div>
          )}
        </div>
      )}
    </>
  );
};
